import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import { Col, Flex, Form, Row } from 'antd'
import { debounce } from 'lodash'
import { ReactNode, useEffect, useMemo, useRef } from 'react'

const DEBOUNCE_TIME = 500

type SBSearchFilterItem = {
  name: string
  input: ReactNode
  label?: string
  type: 'search' | 'filter'
  initialValue?: any
  style?: React.CSSProperties
}
type SBFilterButtonItem = {
  input: ReactNode
  type: 'button'
}
type SBFilterItem = SBSearchFilterItem | SBFilterButtonItem

interface Props {
  left: SBFilterItem[]
  right?: SBFilterItem[]
  onValuesChange?: (
    filters: NonNullable<TableParams['filters']>,
    search: NonNullable<TableParams['search']>
  ) => void
  initialValues?: Record<string, any>
  /**
   * Lets the bar wrap instead of sharing the row in halves: each control keeps
   * the width it declares, the `left` group stays on the first row and the
   * `right` group sits at the end of it, or drops to a second row aligned
   * right when they do not fit together.
   */
  wrapRows?: boolean
}

const TableFilters = ({
  left,
  right = [],
  onValuesChange,
  initialValues,
  wrapRows = false,
}: Props) => {
  const [form] = Form.useForm()

  const searchFields = useMemo(
    () =>
      [...left, ...right]
        .filter((f) => f.type === 'search')
        .map((f) => (f as SBSearchFilterItem).name),
    [left, right]
  )

  const filterFields = useMemo(
    () =>
      [...left, ...right]
        .filter((f) => f.type === 'filter')
        .map((f) => (f as SBSearchFilterItem).name),
    [left, right]
  )

  /**
   * Callers pass a fresh inline `onValuesChange` on every render, so keying the
   * debounce on it rebuilt the debounced function each render and the cleanup
   * below cancelled the pending call: a re-render mid-typing silently dropped
   * the search. Read the latest callback through a ref and debounce once.
   */
  const onValuesChangeRef = useRef(onValuesChange)
  useEffect(() => {
    onValuesChangeRef.current = onValuesChange
  }, [onValuesChange])

  const debouncedOnValuesChange = useMemo(
    () =>
      debounce((filters: Record<string, any>, search: Record<string, any>) => {
        onValuesChangeRef.current?.(filters, search)
      }, DEBOUNCE_TIME),
    []
  )

  const isValidSearchValue = (value: any): boolean =>
    value !== undefined && value !== '' && value !== null

  const isSameValue = (a: any, b: any): boolean =>
    !isValidSearchValue(a) && !isValidSearchValue(b) ? true : a === b

  /**
   * Search fields the user has typed in since the last value we published.
   * `initialValues` comes back from the URL 500ms behind the input, so writing
   * it back blindly overwrote whatever was typed in the meantime (the caret
   * jumped to the end and the next keystroke looked like it replaced the last
   * character). A field stays here until the round trip catches up with it.
   */
  const editingFieldsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    return () => {
      debouncedOnValuesChange.cancel()
    }
  }, [debouncedOnValuesChange])

  useEffect(() => {
    if (!initialValues) return

    const applicable = Object.entries(initialValues).filter(([key, value]) => {
      if (!editingFieldsRef.current.has(key)) return true

      // Round trip caught up: the field is in sync again, stop shielding it.
      if (isSameValue(value, form.getFieldValue(key))) {
        editingFieldsRef.current.delete(key)
      }

      return false
    })

    if (applicable.length > 0) {
      form.setFieldsValue(Object.fromEntries(applicable))
    }
  }, [initialValues, form])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onValuesChange={(changedValues, allValues) => {
        const search: Record<string, any> = {}
        const filters: Record<string, any> = {}

        Object.entries(allValues).forEach(([key, value]) => {
          if (searchFields.includes(key)) {
            search[key] = isValidSearchValue(value) ? value : undefined
          } else if (filterFields.includes(key)) {
            filters[key] = value
          }
        })

        const changedKey = Object.keys(changedValues)[0]
        if (searchFields.includes(changedKey)) {
          editingFieldsRef.current.add(changedKey)
          debouncedOnValuesChange(filters, search)
        } else {
          onValuesChange?.(filters, search)
        }
      }}
      layout="vertical"
    >
      <Row className="w-full space-between">
        <Col span={24}>
          <Flex
            justify="space-between"
            wrap={wrapRows}
            gap={wrapRows ? 16 : undefined}
            style={{ marginBottom: 16 }}
          >
            <Flex
              gap={10}
              align="end"
              justify="start"
              wrap
              className={wrapRows ? undefined : 'w-full'}
            >
              {left.map((filter, index) => (
                <ConditionalWrapper
                  key={index}
                  condition={filter.type != 'button'}
                  wrapper={(children) => (
                    <Form.Item
                      style={{
                        marginBottom: 0,
                        width: `calc(${100 / left.length}% - ${((left.length - 1) * 10) / left.length}px)`,
                        maxWidth: wrapRows ? 'none' : '50%',
                        ...(filter as SBSearchFilterItem).style,
                      }}
                      key={index}
                      name={(filter as SBSearchFilterItem).name}
                      label={(filter as SBSearchFilterItem).label}
                      initialValue={(filter as SBSearchFilterItem).initialValue}
                    >
                      {children}
                    </Form.Item>
                  )}
                >
                  {filter.input}
                </ConditionalWrapper>
              ))}
            </Flex>

            <Flex
              gap={10}
              align="end"
              justify="end"
              wrap
              className={wrapRows ? undefined : 'w-full'}
              style={wrapRows ? { marginLeft: 'auto' } : undefined}
            >
              {right.map((filter, index) => (
                <ConditionalWrapper
                  key={index}
                  condition={filter.type != 'button'}
                  wrapper={(children) => (
                    <Form.Item
                      style={{
                        marginBottom: 0,
                        width: `calc(${100 / right.length}% - ${((right.length - 1) * 10) / right.length}px)`,
                        maxWidth: wrapRows ? 'none' : '40%',
                        // Same per-item override as `left`, so a caller can pin
                        // a designed width instead of the computed share.
                        ...(filter as SBSearchFilterItem).style,
                      }}
                      key={index}
                      name={(filter as SBSearchFilterItem).name}
                      label={(filter as SBSearchFilterItem).label}
                    >
                      {children}
                    </Form.Item>
                  )}
                >
                  {filter.input}
                </ConditionalWrapper>
              ))}
            </Flex>
          </Flex>
        </Col>
      </Row>
    </Form>
  )
}

export { TableFilters }
